














































import { Component, Prop, Vue } from 'vue-property-decorator';

import { ICoinsPurchased, IInitiativeFilter } from '@/models/performance';
import {
  EInitiativesDictionary,
  InitiativeLabelDictionary
} from '@/models/initiatives';
import { Campaign } from '@/models/marketingCenter/sellCoins';

import PaginationUtils, {
  EPaginationSortDir,
  IPagination,
  IPaginationTableHeader
} from '@/utils/PaginationUtils';
import DateUtils from '@/utils/DateUtils';

import SectionData from '@/componentsV2/organisms/SectionData/SectionData.vue';
import CoinsPurchasedHistoryList from '@/componentsV2/organisms/CoinsPurchasedHistoryList/CoinsPurchasedHistoryList.vue';
import Trigger from '@/componentsV2/molecules/Trigger/Trigger.vue';
import SellCoinsCampaignListItem from '@/componentsV2/templates/SellCoinsCampaignListItem/SellCoinsCampaignListItem.vue';
import { CustomSelectOption } from '@/components/CustomSelect/CustomSelectModel';

import SellCoinsService from '@/api/SellCoinsService';

@Component({
  components: {
    SectionData,
    CoinsPurchasedHistoryList,
    Trigger,
    SellCoinsCampaignListItem
  }
})
export default class SellCoinsPurchaseHistory extends Vue {
  @Prop() campaignId?: string;
  campaign: Campaign = {
    formatted: {
      pendingRevenue: '0',
      totalRevenue: '--'
    },
    incentiveValue: '--',
    name: '--',
    totalRevenue: '--'
  };

  sellCoinsService = new SellCoinsService();

  transactionStatus: IInitiativeFilter[] = [];
  pagination: IPagination = PaginationUtils.defaultPagination();
  filters: Record<string, any> = {};

  coinsPurchasedHistory: ICoinsPurchased[] = [];
  headers: Record<string, IPaginationTableHeader> = this.campaignId
    ? this.headersCampaignDetail
    : this.headersCampaign;

  search = {
    placeholder: 'Search by name, date or amount',
    term: ''
  };

  firstRequestIsLoading = false;

  get headersCampaignDetail() {
    return {
      externalPaymentDate: {
        label: 'Date',
        sortDir: EPaginationSortDir.DESC,
        sortable: true
      },
      externalCustomerName: {
        label: 'Customer',
        sortable: true
      },
      balanceStatus: {
        label: 'Status',
        sortable: true
      },
      amountOfCoins: {
        label: 'Amount of Coins',
        sortable: true
      },
      grossAmount: {
        label: 'Gross amount',
        sortable: true
      },
      netAmount: {
        label: 'Net revenue',
        sortable: true
      }
    };
  }

  get headersCampaign() {
    return {
      externalPaymentDate: {
        label: 'Date',
        sortable: true,
        sortDir: EPaginationSortDir.DESC
      },
      externalCustomerName: {
        label: 'Customer',
        sortable: true
      },
      balanceStatus: {
        label: 'Status',
        sortable: true
      },
      campaignName: {
        label: 'Campaign name',
        sortable: true
      },
      amountOfCoins: {
        label: 'Amount of Coins',
        sortable: true
      },
      grossAmount: {
        label: 'Gross amount',
        sortable: true
      },
      netAmount: {
        label: 'Net revenue',
        sortable: true
      }
    };
  }

  get tPrefix() {
    return 'sellCoins.coinsPurchasedHistory';
  }

  get filterGATracking() {
    return {
      eventCategory: 'Purchased Coins History - Tag Filter',
      eventAction: 'click'
    };
  }

  get gaTracking() {
    return {
      search: {
        eventCategory: 'Purchased Coins History - Search',
        eventAction: 'change'
      },
      select: {
        eventCategory: 'Purchased Coins History - Select',
        eventAction: 'change'
      }
    };
  }

  formattedCoinsPurchasedHistory(coinsPurchasedHistory: ICoinsPurchased[]) {
    return coinsPurchasedHistory.map(item => ({
      ...item,

      formatted: {
        ...item.formatted,
        externalPaymentDate: item.businessTransaction.externalPaymentDate
          ? DateUtils.formattedDate({
              date: item.businessTransaction.externalPaymentDate,
              applyTimezone: true
            })
          : '-',
        transactionStatus:
          InitiativeLabelDictionary[item.businessTransaction.transactionStatus]
      }
    }));
  }

  async loadCoinsPurchasedHistory({
    clearList = false,
    isFirstRequest = false
  } = {}) {
    let endpointPath = '/admin/business-wallet/purchase-history';

    if (clearList) {
      this.pagination = PaginationUtils.defaultPagination();
    }

    if (!this.pagination.mustCallApi || this.firstRequestIsLoading) return;
    if (isFirstRequest) this.firstRequestIsLoading = true;

    if (this.campaignId) {
      endpointPath = `/admin/sell-coin-campaign/${this.campaignId}/purchases`;

      // Drive the Campaign revenue card with the same range as the listing so
      // the top total always matches the sum of what the list shows.
      const dateParams: Record<string, any> = {};
      if (this.filters.from) dateParams.from = this.filters.from;
      if (this.filters.to) dateParams.to = this.filters.to;

      const { data } = await this.sellCoinsService.detailOfCampaign(
        this.campaignId,
        dateParams
      );

      this.campaign = data;
    }

    if (this.pagination.next) {
      endpointPath = this.pagination.next;
    }

    this.$showLoaderWithLocker();

    const response = await this.sellCoinsService.getCoinsPurchaseHistory(
      endpointPath,
      this.filters
    );

    this.$hideLoader();
    if (isFirstRequest) this.firstRequestIsLoading = false;

    if (response.status !== 200) return;

    const { items, links, filters } = response.data;

    if (filters) {
      this.transactionStatus = filters.status.map(
        (item: EInitiativesDictionary) => {
          return { label: InitiativeLabelDictionary[item], type: item };
        }
      );
    }

    Object.assign(this.pagination, {
      next: links.next,
      mustCallApi: !!links.next
    });

    if (clearList) {
      this.coinsPurchasedHistory = this.formattedCoinsPurchasedHistory(items);

      return;
    }

    this.coinsPurchasedHistory = [
      ...this.coinsPurchasedHistory,
      ...this.formattedCoinsPurchasedHistory(items)
    ];

    PaginationUtils.makeNewRequest(
      {
        triggerClass: '.infinite-trigger',
        hasPagination: !!this.pagination.next
      },
      this.loadCoinsPurchasedHistory
    );
  }

  handleSort(field: string) {
    this.headers = PaginationUtils.getUpdatedHeaders(field, this.headers);

    const sortDir = this.headers[field].sortDir;

    this.filters = {
      ...this.filters,
      sort: `${field}:${sortDir}`
    };

    this.$analytics({
      eventAction: 'click',
      eventCategory: 'Purchased Coins History - Sort',
      eventLabel: `[${sortDir}] ${this.headers[field].label} column`
    });

    this.loadCoinsPurchasedHistory({ clearList: true });
  }

  handleCustomFilterChange(value: CustomSelectOption[]) {
    delete this.filters.status;

    const status = value[0].uuid;
    if (status) {
      this.filters = {
        ...this.filters,
        status
      };
    }
    this.loadCoinsPurchasedHistory({ clearList: true });
  }
  handleSearchFilterChange() {
    delete this.filters.search;

    if (this.search.term) {
      this.filters = {
        ...this.filters,
        search: this.search.term
      };
    }

    this.loadCoinsPurchasedHistory({ clearList: true });
  }

  handleFilterChange(transactionStatus: string[]) {
    this.filters = {
      ...this.filters,
      transactionStatus
    };

    this.loadCoinsPurchasedHistory({ clearList: true });
  }

  // The date range now lives in the host header (DS DatePicker). The host posts
  // the selected range across the ExternalContent bridge as a
  // `sellCoins:periodChange` message; here we apply it to both fetches. `from`/`to`
  // arrive as ISO strings (Dates are JSON-serialized across the bridge).
  onHostPeriodChange(event: MessageEvent) {
    if (!event.data || event.data.type !== 'sellCoins:periodChange') return;

    const range = event.data.data || {};

    this.filters = this.filtersForPeriod(range);
    this.loadCoinsPurchasedHistory({ clearList: true });
  }

  // Applies the selected range to the current filters, replacing any previous
  // from/to. An empty range (Lifetime / cleared) leaves them out → all-time.
  filtersForPeriod(range: { from?: string; to?: string }) {
    const { from, to, ...rest } = this.filters;

    if (range.from && range.to) {
      return { ...rest, from: range.from, to: range.to };
    }

    return rest;
  }

  created() {
    window.addEventListener('message', this.onHostPeriodChange);
  }

  beforeDestroy() {
    window.removeEventListener('message', this.onHostPeriodChange);
  }

  mounted() {
    this.loadCoinsPurchasedHistory({ clearList: true, isFirstRequest: true });
  }
}
